import React, { useEffect, useState, useRef } from "react";
import { RouteComponentProps } from "../../../models/RouteComponent";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { ContextMenu } from "primereact/contextmenu";
import { Dialog } from "primereact/dialog";
import { Typography } from "@mui/joy";
import { Toast } from "primereact/toast";
import { deleteIncident, IncidentItem } from "../../../service/incidentService";

import { formatLocalDateAsUtc } from "../../billing/common";
import dayjs from "dayjs";
import IncidentAdd from "./incident-Add";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useSession } from "../../../context/SessionContext";
import { Calendar, Select } from "../../../components/Shared/Forms";

import styles from "./incident.module.css";
import useApiService from "../../../hooks/useApiService";
import { EmployeeDetail } from "../../../models/employee.interface";
import { incidentType } from "../../../models/incidents.interface";
import { useSiteContext } from "../../../context/siteContext";

interface ExtendedRouteComponentProps extends RouteComponentProps {
  setGlobalLoading?: (loading: boolean) => void;
}

export default function IncidentView(props: ExtendedRouteComponentProps) {
  const { session } = useSession();
  const { site } = useSiteContext();

  // Context menu and selection state
  const [selectedRow, setSelectedRow] = useState<IncidentItem | null>(null);
  const cm = useRef<ContextMenu>(null);
  const toast = useRef<Toast>(null);

  // State for modal
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);

  // State for filters
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    const lastDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return [firstDay, lastDay];
  });
  const [employeeId, setEmployeeId] = useState<string>("");
  const [siteId, setSiteId] = useState<string>("");
  const [typeId, setTypeId] = useState<string>("");

  // State for data
  const [data, setData] = useState<IncidentItem[]>([]);

  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationParams, setPaginationParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });

  const {
    data: employeeData,
    loading: loadingEmployees,
    revalidate,
  } = useApiService<EmployeeDetail[]>({
    url: "site-employee",
    method: "GET",
    apiName: "core",
    cache: {
      enabled: true,
      strategy: "cache-first",
      key: `core::GET::employee::site::${site?.id}`,
    },
  });

  const { data: incidentTypesData, loading: loadingIncidentTypes } = useApiService<incidentType[]>({
    url: "incident/type",
    method: "GET",
    apiName: "core",
    cache: {
      enabled: true,
      strategy: "cache-first",
      key: `core::GET::incident/type::client::${session.client_id}`,
    },
  });

  const [dateFrom, dateTo] = dateRange;

  const { loading: loadingIncidents, fetchData: fetchIncidents } = useApiService<{
    rows: IncidentItem[];
    total: string;
  }>({
    url: "incident",
    params: {
      dateFrom: formatLocalDateAsUtc(dateFrom, false),
      dateTo: formatLocalDateAsUtc(dateTo, true),
      limit: paginationParams.rows,
      page: paginationParams.page,
      clientId: session.client_id,
      // employeeId: employeeId || undefined,
      siteId: session.site_id ? session.site_id : siteId || undefined,
      // typeId: typeId || undefined,
    },
    method: "GET",
    onSuccess: res => {
      setTotalRecords(parseInt(res.total));
      setData(res.rows);
    },
  });

  // Context menu items
  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        if (selectedRow) {
          setIsAddModalVisible(true);
        }
      },
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: async () => {
        if (selectedRow) {
          confirmDialog({
            message: "Delete Incident?",
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            accept: async () => {
              try {
                await deleteIncident(selectedRow.id);
                toast.current?.show({
                  severity: "success",
                  summary: "Deleted",
                  detail: "Incident successfully deleted",
                  life: 3000,
                });

                let params: {
                  dateFrom: string;
                  dateTo: string;
                  limit: number;
                  page: number;
                  clientId?: string;
                  siteId?: string;
                  employeeId?: string;
                  typeId?: string;
                } = {
                  dateFrom: formatLocalDateAsUtc(dateFrom, false),
                  dateTo: formatLocalDateAsUtc(dateTo, true),
                  limit: paginationParams.rows,
                  page: paginationParams.page,
                  clientId: session.client_id,
                  siteId: session.site_id ? session.site_id : siteId || undefined,
                };

                if (employeeId) {
                  params.employeeId = employeeId;
                }
                if (typeId) {
                  params.typeId = typeId;
                }

                await fetchIncidents({
                  fetchParams: params,
                });
              } catch (error) {
                toast.current?.show({
                  severity: "error",
                  summary: "Error",
                  detail: "Failed to delete incident",
                  life: 3000,
                });
              }
            },
          });
        }
      },
    },
  ];

  const handleFilterChange = async (filter: "dateRange" | "employee" | "type", value: any) => {
    let params: {
      dateFrom: string;
      dateTo: string;
      limit: number;
      page: number;
      clientId?: string;
      siteId?: string;
      employeeId?: string;
      typeId?: string;
    } = {
      dateFrom: formatLocalDateAsUtc(dateFrom, false),
      dateTo: formatLocalDateAsUtc(dateTo, true),
      limit: paginationParams.rows,
      page: paginationParams.page,
      clientId: session.client_id,
      siteId: session.site_id ? session.site_id : siteId || undefined,
    };
    switch (filter) {
      case "dateRange":
        setDateRange(value);
        if (value && value.length > 1) {
          if (value[0] && value[1]) {
            params.dateFrom = formatLocalDateAsUtc(value[0], false);
            params.dateTo = formatLocalDateAsUtc(value[1], true);
          }
        }
        break;
      case "employee":
        setEmployeeId(value);
        if (value) {
          params.employeeId = value;
        } else {
          delete params.employeeId;
        }
        break;
      case "type":
        setTypeId(value);
        if (value) {
          params.typeId = value;
        } else {
          delete params.typeId;
        }
        break;
    }

    await fetchIncidents({
      fetchParams: params,
    });
  };

  const onPage = (event: {
    filters?: any;
    first: number;
    page: number;
    rows: number;
    totalPages: number;
  }) => {
    setPaginationParams({
      first: event.first,
      rows: event.rows,
      page: event.page,
    });
    let params: {
      dateFrom: string;
      dateTo: string;
      limit: number;
      page: number;
      clientId?: string;
      siteId?: string;
      employeeId?: string;
      typeId?: string;
    } = {
      dateFrom: formatLocalDateAsUtc(dateFrom, false),
      dateTo: formatLocalDateAsUtc(dateTo, true),
      limit: paginationParams.rows,
      page: paginationParams.page,
      clientId: session.client_id,
      siteId: session.site_id ? session.site_id : siteId || undefined,
    };

    if (employeeId) {
      params.employeeId = employeeId;
    }
    if (typeId) {
      params.typeId = typeId;
    }

    params.limit = event.rows;
    params.page = event.page;

    fetchIncidents({
      fetchParams: params,
    });
  };

  const addClick = () => {
    setSelectedRow(null);
    setIsAddModalVisible(true);
  };

  return (
    <div>
      <Toast ref={toast} />
      <ContextMenu model={menuItems} ref={cm} />
      <ConfirmDialog />

      <Typography level="h2" component="h1">
        Incident History
      </Typography>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="button"
          icon="pi pi-plus"
          label="Add"
          className="p-button-outlined"
          size="small"
          style={{ color: "#2dd36f" }}
          onClick={addClick}
        />
      </div>

      <br />

      {/* Horizontal Filters */}
      <div className={styles["filter-actions"]}>
        <Calendar
          value={dateRange}
          label={"Date range"}
          onChange={async e => {
            // console.log("dateRange", e.value);
            // setDateRange(e.value);
            await handleFilterChange("dateRange", e.value);
          }}
          rangeMode={"range"}
          showIcon={true}
          className={styles["item-width"]}
        />

        <Select
          options={incidentTypesData?.map(type => ({ value: type.id, label: type.name })) ?? []}
          label={"Incident Type"}
          onChange={async e => {
            await handleFilterChange("type", e.value);
          }}
          value={typeId}
          placeholder={"Incident Type"}
          showClear={true}
          className={styles["item-width"]}
          loading={loadingIncidentTypes}
        />

        <Select
          options={
            employeeData?.map(employee => ({ value: employee.id, label: employee.full_name })) ?? []
          }
          value={employeeId}
          label={"Employee"}
          onChange={async e => {
            // setEmployeeId(e.value);
            await handleFilterChange("employee", e.value);
          }}
          placeholder={"Select employee"}
          showClear={true}
          className={styles["item-width"]}
          loading={loadingEmployees}
        />
      </div>

      {/* Data Table */}
      <DataTable
        value={data}
        contextMenuSelection={selectedRow as Record<string, any>}
        onContextMenuSelectionChange={e => setSelectedRow(e.value as IncidentItem)}
        onContextMenu={e => cm.current?.show(e.originalEvent)}
        selectionMode="single"
        selection={selectedRow}
        onSelectionChange={(e: { value: IncidentItem; originalEvent: React.SyntheticEvent }) => {
          setSelectedRow(e.value);
          cm.current?.show(e.originalEvent);
        }}
        loading={loadingIncidents}
        className="p-datatable-sm"
        lazy
        paginator
        rowsPerPageOptions={[10, 20, 50, 100, 1000]}
        first={paginationParams.first}
        rows={paginationParams.rows}
        // @ts-ignore
        onPage={onPage}
        totalRecords={totalRecords}
        tableStyle={{ minWidth: "50rem" }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        {siteId ? null : <Column field="site_name" header="Site" style={{ minWidth: "200px" }} />}
        <Column
          field="created_at"
          header="Date"
          body={rowData => dayjs(rowData.created_at).format("YYYY/MM/DD HH:mm")}
          style={{ minWidth: "200px" }}
        />
        <Column field="description" header="Description" style={{ minWidth: "300px" }} />
        <Column field="employee_name" header="Employee" style={{ minWidth: "200px" }} />
        <Column field="type_name" header="Type" style={{ minWidth: "200px" }} />
      </DataTable>

      {/* Add/Edit Modal */}
      <Dialog
        header={selectedRow?.id ? "Edit Incident" : "Add Incident"}
        visible={isAddModalVisible}
        style={{ width: "80vw" }}
        onHide={() => {
          setSelectedRow(null);
          setIsAddModalVisible(false);
        }}
        draggable={false}
      >
        <IncidentAdd
          modalProps={{
            id: selectedRow?.id,
            type_id: selectedRow?.type_id,
            onSuccess: async () => {
              setSelectedRow(null);
              setIsAddModalVisible(false);
              let params: {
                dateFrom: string;
                dateTo: string;
                limit: number;
                page: number;
                clientId?: string;
                siteId?: string;
                employeeId?: string;
                typeId?: string;
              } = {
                dateFrom: formatLocalDateAsUtc(dateFrom, false),
                dateTo: formatLocalDateAsUtc(dateTo, true),
                limit: paginationParams.rows,
                page: paginationParams.page,
                clientId: session.client_id,
                siteId: session.site_id ? session.site_id : siteId || undefined,
              };

              if (employeeId) {
                params.employeeId = employeeId;
              }
              if (typeId) {
                params.typeId = typeId;
              }

              await fetchIncidents({
                fetchParams: params,
              });
            },
            toast: toast,
            setGlobalLoading: props.setGlobalLoader,
          }}
        />
      </Dialog>
    </div>
  );
}
